import { useEffect, lazy, Suspense, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import { ThreeDotLoader } from '../../app/layout/LoadingComponent';
import DataGridWithForm from './dataGridWithForm';
import './styles.css';
import UnauthorizedMessage from '../UnauthorizedMessage';
import SectionsModal from '../MediaAssets/Sections/SectionsModal';

const Card = lazy(() => import('./card'));
const Card2 = lazy(() => import('./card2'));
const FormRegisterDyDB = lazy(() => import('./form'));
const TableReport = lazy(() => import('./dataGrid'));
const Workflow = lazy(() => import('./workflow'));
const ProductionPlanning = lazy(() => import('./productionPlanning'));
const Chart = lazy(() => import('./chart'));
const Pivot = lazy(() => import('./pivot'));

function DynamicDashboard() {
  const {
    DynamicDashboardStore,
    userStore,
    commonStore,
    RolesAndPermissionsIDStore,
    dashboardStore,
  } = useStore();
  const { dashboard } = DynamicDashboardStore || {};
  const { rolesIDs } = RolesAndPermissionsIDStore;

  const [isTableUpdated, setIsTableUpdated] = useState(false);

  const copyOfDashboard = useRef<any>({});
  const duplicateLevel = useRef(0);

  let nextRenderAPI = true;

  const { DepartmentNameAddMarkLogin } = userStore;

  useEffect(() => {
    DynamicDashboardStore.dashboard = {};
    DepartmentNameAddMarkLogin();
    // eslint-disable-next-line
  }, []);

  const detailsBasedOnParentPromise = (levelId: string) => {
    return new Promise(async (resolve) => {
      if (!nextRenderAPI) return;

      let result =
        await DynamicDashboardStore.getLevelDetailsBasedOnParent(levelId);

      let permissionData =
        copyOfDashboard.current?.[`${duplicateLevel.current}`]?.data[0]
          ?.permission;

      if (permissionData === 0 || permissionData === 2) {
        nextRenderAPI = false;
      } else {
        nextRenderAPI = true;
      }

      duplicateLevel.current += 1;

      if (result?.length > 0) {
        copyOfDashboard.current[`${duplicateLevel.current}`] = {
          data: result,
          selectedId: result[0].levelId,
        };

        if (
          !(result?.[0]?.type === 'dataGrid' || result?.[0].type === 'form') &&
          result?.[0]?.permission === 1
        ) {
          await detailsBasedOnParentPromise(result[0].levelId);
        }

        DynamicDashboardStore.dashboard = copyOfDashboard.current;

        resolve('');
      } else if (result?.length === 0) {
        copyOfDashboard.current[`${duplicateLevel.current}`] = {
          data: [],
          selectedId: '',
        };
        DynamicDashboardStore.dashboard = copyOfDashboard.current;
        DynamicDashboardStore.staticSelectedCard = '';
        resolve('');
      }
    });
  };

  useEffect(() => {
    (async () => {
      DynamicDashboardStore.isDashboardLoading = true;
      let result = await DynamicDashboardStore.getLevelDetails();
      if (result) {
        duplicateLevel.current += 1;
        // static changes for LVPCMS-AM
        let index = 0;
        if (commonStore.markLoginStaticRegisterDemoChages) {
          if (userStore?.user?.roleName.includes('Accountant')) {
            index = 2;
          }
          if (userStore?.user?.roleName.includes('Conformist')) {
            index = 5;
          }
          if (userStore?.user?.roleName.includes('Colourist')) {
            index = 6;
          }
          if (userStore?.user?.roleName.includes('Line producer')) {
            index = 7;
          }
          if (userStore?.user?.roleName.includes('Data Management')) {
            index = 8;
          }
        }

        if (result[index]) {
          copyOfDashboard.current[`${duplicateLevel.current}`] = {
            data: result,
            selectedId: result[index].levelId,
          };
          DynamicDashboardStore.staticSelectedCard = result[index].name;

          if (result[index].permission === 1)
            await detailsBasedOnParentPromise(result[index].levelId);
        }
      }

      DynamicDashboardStore.dashboard = copyOfDashboard.current;
      copyOfDashboard.current = {};
      duplicateLevel.current = 0;
      DynamicDashboardStore.isDashboardLoading = false;
    })();

    // eslint-disable-next-line
  }, []);

  const nextLevelClickHandler = async (
    level: string,
    levelId: string,
    index: number,
    permission: number,
  ) => {
    if (permission === 0) return;
    DynamicDashboardStore.isDashboardLoading = true;

    let indexOfLevel = parseInt(level);
    let dashboardData = Object.keys(DynamicDashboardStore.dashboard);
    let levelCardDetails: any = DynamicDashboardStore.dashboard[level]?.data;
    if (dashboardData) {
      // Find the index of the object with the matching levelId in the data array
      const dataIndex = levelCardDetails?.findIndex(
        (item: any) => item.levelId === levelId,
      );
      if (dataIndex !== -1) {
        // Access the name property based on the dataIndex
        DynamicDashboardStore.staticSelectedCard =
          levelCardDetails[dataIndex].name;
      }
    }

    for (let i = indexOfLevel; i < dashboardData.length; i++) {
      delete DynamicDashboardStore.dashboard[`${i + 1}`];
    }

    copyOfDashboard.current = DynamicDashboardStore.dashboard;

    copyOfDashboard.current[`${indexOfLevel}`] = {
      ...copyOfDashboard.current[`${indexOfLevel}`],
      selectedId: levelId,
    };

    duplicateLevel.current = indexOfLevel;

    await detailsBasedOnParentPromise(levelId).then(() => {
      DynamicDashboardStore.isDashboardLoading = false;
      copyOfDashboard.current = {};
      duplicateLevel.current = 0;
    });
  };

  const productionPlanningHandler = (
    level: string,
    levelId: string,
    _index: number,
    permission: number,
  ) => {
    // if (permission === 0) return;

    DynamicDashboardStore.isDashboardLoading = true;

    let indexOfLevel = parseInt(level);

    for (let i = 1; i <= indexOfLevel; i++) {
      copyOfDashboard.current[`${i}`] = DynamicDashboardStore.dashboard[`${i}`];
    }

    copyOfDashboard.current[`${level}`] = {
      ...copyOfDashboard.current[`${level}`],
      selectedId: levelId,
    };
    DynamicDashboardStore.dashboard = copyOfDashboard.current;
    duplicateLevel.current = parseInt(level);

    DynamicDashboardStore.dashboard = copyOfDashboard.current;
    copyOfDashboard.current = {};
    duplicateLevel.current = 0;

    DynamicDashboardStore.isDashboardLoading = false;
  };

  let styleHandler = (
    flexBasis: string,
    index: number,
    permission: number,
    name: string,
  ) => {
    return {
      flexBasis: index === 0 ? `${flexBasis}%` : `calc(${flexBasis}% - 20px)`,
      pointerEvents: permission === 0 ? 'none' : ('auto' as any),
      opacity: permission === 0 ? '.5' : '1',
    };
  };

  const renderHandler = (
    data: any,
    level: string,
    index: number,
    isSelected: boolean,
  ) => {
    // if (data.permission === 2) return <></>;

    switch (data?.type) {
      case 'card':
        return data.data.style !== 'style 2' ? (
          <Card
            cardDetails={data}
            isSelected={isSelected}
            onClickHandler={() =>
              nextLevelClickHandler(level, data.levelId, index, data.permission)
            }
          />
        ) : (
          <Card2
            isSelected={isSelected}
            cardDetails={data}
            onClickHandler={() =>
              nextLevelClickHandler(level, data.levelId, index, data.permission)
            }
          />
        );
      case 'form':
        let cancelBtnMode = rolesIDs.filter(
          (data) => data.itemId === 'dd_cancel_btn_form',
        )?.[0]?.mode;
        let submitBtnMode = rolesIDs.filter(
          (data) => data.itemId === 'dd_submit_btn_form',
        )?.[0]?.mode;

        return (
          <>
            {data.masterName === 'Items' ? (
              <SectionsModal
                title="Dyn-Item"
                setIsTableUpdated={setIsTableUpdated}
              />
            ) : (
              <FormRegisterDyDB
                permission={data.permission}
                flexBasis={data.flexBasis}
                masterId={data.masterId}
                name={data.name}
                index={index}
                setIsTableUpdated={setIsTableUpdated}
                metaData={JSON.parse(data.masterElement)}
                cancelBtnMode={cancelBtnMode === undefined ? 1 : cancelBtnMode}
                submitBtnMode={submitBtnMode === undefined ? 1 : submitBtnMode}
                automaticPdf={data.automaticPdf}
              />
            )}
          </>
        );
      case 'dataGrid':
        let downloadBtnMode = rolesIDs.filter(
          (data) => data.itemId === 'dd_download_btn_table',
        )?.[0]?.mode;
        let deleteBtnMode = rolesIDs.filter(
          (data) => data.itemId === 'dd_delete_btn_table',
        )?.[0]?.mode;
        let editBtnMode = rolesIDs.filter(
          (data) => data.itemId === 'dd_edit_btn_table',
        )?.[0]?.mode;

        return (
          <TableReport
            flexBasis={data.flexBasis}
            masterId={data.masterId}
            masterName={data.masterName}
            metaData={JSON.parse(data.masterElement)}
            automaticPdf={data.automaticPdf}
            isMaster={!!data.isMaster}
            name={data.name}
            assetType={data.assetType}
            elementType={data.elementType}
            index={index}
            isTableUpdated={isTableUpdated}
            setIsTableUpdated={setIsTableUpdated}
            downloadBtnMode={
              downloadBtnMode === undefined ? 1 : downloadBtnMode
            }
            deleteBtnMode={deleteBtnMode === undefined ? 1 : deleteBtnMode}
            editBtnMode={editBtnMode === undefined ? 1 : editBtnMode}
          />
        );
      case 'workflow':
        return (
          <Workflow
            cardDetails={data}
            isSelected={isSelected}
            index={index}
            onClickHandler={() =>
              productionPlanningHandler(
                level,
                data.levelId,
                index,
                data.permission,
              )
            }
          />
        );
      case 'pivotPlaceholder':
        return (
          <Pivot
            isSelected={isSelected}
            data={{
              name: data.name,
              type: data.type,
              pivotId: data.pivot.pivotId,
            }}
          />
        );
      case 'chart':
        return (
          <Chart
            chartName={data.chart.chartName}
            chartType={data.chart.chartType}
            name={data.name}
          />
        );
      case 'tableWithForm':
        return (
          <DataGridWithForm
            tableWithForm={data.tableWithForm}
            name={data.name}
            setIsTableUpdated={setIsTableUpdated}
            isTableUpdated={isTableUpdated}
          />
        );
      case 'productionAndPlanning':
        return (
          <ProductionPlanning
            permission={data.permission}
            levelId={data.levelId}
            icon={data.icon}
            name={data.name}
            flexBasis={data.flexBasis}
            isSelected={isSelected}
            index={index}
            needChart={data.prod.needChart}
            onClickHandler={() =>
              productionPlanningHandler(
                level,
                data.levelId,
                index,
                data.permission,
              )
            }
          />
        );
      default:
        return (
          <ProductionPlanning
            permission={data.permission}
            levelId={data.levelId}
            icon={data.icon}
            name={data.name}
            flexBasis={data.flexBasis}
            isSelected={data.isSelected}
            index={index}
            onClickHandler={() =>
              productionPlanningHandler(
                level,
                data.levelId,
                index,
                data.permission,
              )
            }
          />
        );
    }
  };

  return (
    <>
      {dashboardStore.dashboard.loadingdata !== 'loading' &&
        !commonStore.filterModeOne?.includes(
          'a69e4760-5da1-4009-9098-e20fda89e7d8',
        ) && <UnauthorizedMessage />}
      {commonStore.filterModeOne.includes(
        'a69e4760-5da1-4009-9098-e20fda89e7d8',
      ) && (
        <div className="dynamic-dashboard-wrapper">
          {Object.entries(dashboard)?.map(
            ([key, levelDetails]: [key: any, levelDetails: any]) => (
              <div className="levelWrapper">
                {(() => {
                  const filteredData = levelDetails.data.filter(
                    (item: any) => item.permission !== 2,
                  );

                  if (
                    filteredData.length === 1 &&
                    filteredData[0].type === 'card'
                  ) {
                    if (!levelDetails?.autoClicked) {
                      levelDetails.autoClicked = true;
                      const autoClickData = filteredData[0];
                      nextLevelClickHandler(
                        key,
                        autoClickData.levelId,
                        0,
                        autoClickData.permission,
                      );
                    }
                  } else {
                    return (
                      <>
                        {levelDetails.data &&
                          filteredData.map((data: any, index: any) => {
                            // Check for valid flexBasis value before using charAt
                            const dynamicStyle =
                              data.flexBasis && data.flexBasis.charAt(0) !== '0'
                                ? {
                                    ...styleHandler(
                                      data.flexBasis,
                                      index,
                                      data.permission,
                                      data.name,
                                    ),
                                    ...(data.type === 'tableWithForm'
                                      ? {
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          flexWrap: 'wrap',
                                          gap: '20px',
                                        }
                                      : {}),
                                  }
                                : { width: '100%' };

                            return (
                              <div
                                style={dynamicStyle as any}
                                key={data.levelId}
                              >
                                <Suspense fallback={<></>}>
                                  {renderHandler(
                                    data,
                                    key,
                                    index,
                                    data.levelId === levelDetails.selectedId,
                                  )}
                                </Suspense>
                              </div>
                            );
                          })}
                      </>
                    );
                  }
                })()}
              </div>
            ),
          )}
          {DynamicDashboardStore.isDashboardLoading && (
            <ThreeDotLoader className="dy-db-three-dot-loader " />
          )}
        </div>
      )}
    </>
  );
}

export default observer(DynamicDashboard);
